import { useMutation, useQuery, useQueryClient } from 'react-query';

import { VehicleDefaultClass } from '../../models/VehicleDefaultClass';
import vehicleDefaultClassService from '../../services/vehicleDefaultClassService';

export const useVehicleDefaultClasses = () => {
  const { data, status } = useQuery('vehicleDefaultClasses', () =>
    vehicleDefaultClassService.getAll()
  );

  return {
    status,
    vehicleDefaultClasses: data || {},
  };
};

export const useVehicleDefaultClassesByCarrier = (carrierId?: number) => {
  const { data, status } = useQuery(
    ['vehicleDefaultClasses', carrierId],
    () => vehicleDefaultClassService.getByCarrier(carrierId),
    {
      enabled: !!carrierId,
    }
  );

  return {
    status,
    vehicleDefaultClasses: data || {},
  };
};

export const useVehicleDefaultClassCreate = () => {
  const queryClient = useQueryClient();

  return useMutation<VehicleDefaultClass, Error, VehicleDefaultClass>(
    vehicleDefaultClassService.create,
    {
      onSuccess: (data) => {
        // Invalidate both general and carrier-specific queries
        queryClient.invalidateQueries('vehicleDefaultClasses');
        if (data.carrierId) {
          queryClient.invalidateQueries(['vehicleDefaultClasses', data.carrierId]);
        }
      },
    }
  );
};

export const useVehicleDefaultClassUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<VehicleDefaultClass, Error, VehicleDefaultClass>(
    vehicleDefaultClassService.update,
    {
      onSuccess: (data) => {
        // Invalidate both general and carrier-specific queries
        queryClient.invalidateQueries('vehicleDefaultClasses');
        if (data.carrierId) {
          queryClient.invalidateQueries(['vehicleDefaultClasses', data.carrierId]);
        }
      },
    }
  );
};

export const useVehicleDefaultClassRemove = () => {
  const queryClient = useQueryClient();

  return useMutation<VehicleDefaultClass, Error, VehicleDefaultClass>(
    vehicleDefaultClassService.remove,
    {
      onSuccess: (data) => {
        // Invalidate both general and carrier-specific queries
        queryClient.invalidateQueries('vehicleDefaultClasses');
        if (data.carrierId) {
          queryClient.invalidateQueries(['vehicleDefaultClasses', data.carrierId]);
        }
      },
    }
  );
};

export const useVehicleDefaultClassUpdateByCarrier = (carrierId?: number) => {
  const queryClient = useQueryClient();

  return useMutation<Record<string, number[]>, Error, VehicleDefaultClass[]>(
    (vehicleDefaultClasses: VehicleDefaultClass[]) =>
      vehicleDefaultClassService.updateByCarrier(carrierId, vehicleDefaultClasses),
    {
      onSuccess: (data) => {
        // Update the carrier-specific cache with the new format
        if (carrierId) {
          queryClient.setQueryData<Record<string, number[]>>(
            ['vehicleDefaultClasses', carrierId],
            data
          );
        }
        // Also invalidate the general query
        queryClient.invalidateQueries('vehicleDefaultClasses');
      },
    }
  );
};

export const useVehicleDefaultClassUpdateGlobal = () => {
  const queryClient = useQueryClient();

  return useMutation<Record<string, number[]>, Error, VehicleDefaultClass[]>(
    vehicleDefaultClassService.updateGlobal,
    {
      onSuccess: (data) => {
        // Update the general cache
        queryClient.setQueryData<Record<string, number[]>>('vehicleDefaultClasses', data);
        // Also invalidate all carrier-specific queries
        queryClient.invalidateQueries(['vehicleDefaultClasses']);
      },
    }
  );
};
